import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from '@/components/atoms/skeleton';
import { Badge } from '@/components/atoms/badge';
import { useRevenueRecognitionSummary } from '@/hooks/settings/useRevenueRecognition';

export const Route = createFileRoute('/_dashboard/dashboard/settings/deferred-revenue')({
  component: DeferredRevenuePage,
});

/** Formatter for whatever currency the tenant's schedules are denominated in. */
function formatterFor(currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency });
}

/** `2026-01` -> `Jan 2026`. */
function monthLabel(month: string): string {
  const [year, rawMonth] = month.split('-');
  const index = Number(rawMonth) - 1;
  const name = new Date(Date.UTC(2000, index, 1)).toLocaleString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });
  return `${name} ${year}`;
}

/**
 * NOT-596 — deferred plan revenue.
 *
 * The job this screen does: answer "how much service do we still owe, and
 * when does it become revenue?" — the question a lender, an accountant, or a
 * buyer's quality-of-earnings review asks. So the liability leads, the
 * schedule supports it, and drift is called out rather than buried.
 */
function DeferredRevenuePage() {
  const { data, isLoading, isError } = useRevenueRecognitionSummary();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="text-sm text-muted-foreground">
        Deferred revenue couldn&apos;t be loaded. Try again in a moment.
      </p>
    );
  }

  const currency = formatterFor(data.currency ?? 'USD');
  const hasSchedules = data.openScheduleCount > 0;
  // Tolerate a response from a backend deployed before these fields existed —
  // a version-skewed rollout must degrade to "no extra currencies", not crash.
  const otherCurrencies = data.otherCurrencies ?? [];
  const maxMonth = data.months.reduce(
    (max, row) => Math.max(max, row.recognized + row.scheduled),
    0,
  );

  return (
    <div className="space-y-8">
      <section>
        <h2 className="mb-1 text-base font-semibold">Deferred plan revenue</h2>
        <p className="mb-5 text-sm text-muted-foreground">
          Money collected for plan service you haven&apos;t delivered yet. It sits as a liability
          until it&apos;s earned.
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Metric label="Deferred balance" value={currency.format(data.deferredBalance)} emphasis />
          <Metric label="Recognized to date" value={currency.format(data.totalRecognized)} />
          <Metric
            label="Total contracted"
            value={currency.format(data.totalContracted)}
            help={`${data.openScheduleCount} open ${
              data.openScheduleCount === 1 ? 'plan' : 'plans'
            }`}
          />
        </div>

        {otherCurrencies.length > 0 && (
          <p className="mt-4 text-sm text-muted-foreground">
            Also deferred in other currencies:{' '}
            {otherCurrencies
              .map((row) => formatterFor(row.currency).format(row.deferredBalance))
              .join(', ')}
            . Shown separately — mixing currencies into one total would be meaningless.
          </p>
        )}

        {data.failedEntryCount > 0 && (
          <p className="mt-4 text-sm text-destructive">
            {data.failedEntryCount} {data.failedEntryCount === 1 ? 'entry' : 'entries'} failed to
            post to QuickBooks. Until they clear, the liability in QuickBooks won&apos;t match the
            balance above.
          </p>
        )}
      </section>

      <section className="border-t border-border pt-6">
        <h2 className="mb-1 text-base font-semibold">Recognition schedule</h2>
        <p className="mb-5 text-sm text-muted-foreground">When the balance turns into revenue.</p>

        {!hasSchedules || data.months.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing deferred yet. Plan charges show up here once deferral is turned on under
            QuickBooks settings.
          </p>
        ) : (
          <ul className="space-y-2">
            {data.months.map((row) => {
              const total = row.recognized + row.scheduled;
              const width = maxMonth > 0 ? (total / maxMonth) * 100 : 0;
              return (
                <li key={row.month} className="flex items-center gap-4">
                  <span className="w-20 shrink-0 text-sm text-muted-foreground">
                    {monthLabel(row.month)}
                  </span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                  <span className="w-24 shrink-0 text-right text-sm tabular-nums">
                    {currency.format(total)}
                  </span>
                  {row.scheduled > 0 && row.recognized === 0 && (
                    <Badge variant="secondary" className="shrink-0">
                      Scheduled
                    </Badge>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function Metric({
  label,
  value,
  help,
  emphasis,
}: {
  label: string;
  value: string;
  help?: string;
  emphasis?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p
        className={
          emphasis
            ? 'mt-1 text-2xl font-semibold tabular-nums'
            : 'mt-1 text-xl font-medium tabular-nums'
        }
      >
        {value}
      </p>
      {help && <p className="mt-1 text-xs text-muted-foreground">{help}</p>}
    </div>
  );
}
